import * as i0 from '@angular/core';
import { InjectFlags, forwardRef, Component, ViewEncapsulation, HostBinding, Input, HostListener, Directive, Optional, NgModule } from '@angular/core';
import * as i1 from '@angular/forms';
import { NgControl, FormControlName, FormGroupDirective, FormControlDirective, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BaseDirective } from '@eui/components/shared';
import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { Subject, fromEvent } from 'rxjs';
import { takeUntil, debounceTime, startWith } from 'rxjs/operators';
import { CommonModule } from '@angular/common';

class EuiTextareaComponent extends BaseDirective {
    constructor(injector, _elementRef, _renderer) {
        super();
        this.injector = injector;
        this._elementRef = _elementRef;
        this._renderer = _renderer;
        this.id = `eui-textarea_${EuiTextareaComponent.idCounter++}`;
        /** @deprecated it will be removed in next eUI version */
        this.e2eAttr = 'eui-textarea';
        this.destroy$ = new Subject();
    }
    set class(cssClass) {
        super.styleClass = cssClass;
    }
    get class() {
        return [
            this.isInvalid ? `eui-textarea--invalid` : ``,
            super.getCssClasses('eui-textarea'),
        ].join(' ').trim();
    }
    get disabled() {
        return this._disabled ? true : null;
    }
    set disabled(state) {
        // in case it's controlled by NgControl override
        this._disabled = coerceBooleanProperty(state);
        // set BaseDirective Attribute
        super.euiDisabled = this._disabled;
    }
    get readonly() {
        return this._readonly || undefined;
    }
    set readonly(state) {
        this._readonly = coerceBooleanProperty(state);
    }
    get isInvalid() { return this._isInvalid; }
    set isInvalid(value) {
        this._isInvalid = coerceBooleanProperty(value);
        super.euiDanger = this._isInvalid;
    }
    onInputChange(value) {
        this.onChange(value);
    }
    onBlur(value) {
        this.onTouched(value);
    }
    ngOnInit() {
        this.hostEl = this._elementRef.nativeElement;
        this.hostParentEl = this.hostEl.parentElement;
        this.hostWrapperEl = this.createHostWrapperContainer();
        this.valueContainerEl = this.createValueContainer();
        this.handleMarkup();
        // extract the FormControl or NgControl
        this.control = this.injector.get(NgControl, null, InjectFlags.Optional);
        if (this.control instanceof FormControlName) {
            this.control = this.injector.get(FormGroupDirective).getControl(this.control);
        }
        else if (this.control instanceof FormControlDirective) {
            this.control = this.control.form;
        }
        // sets invalid state if control is present
        if (this.control instanceof FormControl || this.control instanceof NgControl) {
            this.isInvalid = this.control.dirty && this.control.touched && this.control.invalid;
            if (this.control instanceof FormControl) {
                this.control.statusChanges
                    .pipe(takeUntil(this.destroy$))
                    .subscribe(status => {
                    this.isInvalid = status === 'INVALID';
                    this.disabled = status === 'DISABLED';
                });
            }
        }
    }
    ngDoCheck() {
        if (this.control instanceof NgControl || this.control instanceof FormControl) {
            // TODO: Delete when https://github.com/angular/angular/issues/30275 is resolved
            this.isInvalid = this.control.invalid && this.control.touched;
        }
    }
    ngOnChanges(changes) {
        if (changes['readonly']) {
            const readonly = changes['readonly'].currentValue;
            if (this.hostWrapperEl && this.valueContainerEl) {
                this.hostWrapperEl.classList.toggle('eui-textarea__wrapper--readonly', !!readonly);
            }
            if (this.hostEl && this.valueContainerEl) {
                this.hostEl.classList.toggle('eui-textarea--readonly', !!readonly);
                this.valueContainerEl.innerText = this.hostEl.value;
            }
        }
        if (changes['euiDisabled']) {
            if (this.control instanceof FormControl) {
                changes['euiDisabled'].currentValue ? this.control.disable() : this.control.enable();
            }
            else {
                this.setDisabledState(changes['euiDisabled'].currentValue);
            }
        }
    }
    ngOnDestroy() {
        this.destroy$.complete();
        this.destroy$.unsubscribe();
        // cleanup DOM leftovers
        if (this.valueContainerEl) {
            try {
                this._renderer.removeChild(this.hostEl.parentElement, this.valueContainerEl);
            }
            catch (e) {
                console.error(e);
            }
        }
        if (this.hostWrapperEl) {
            try {
                this._renderer.removeChild(this.hostEl.parentElement, this.hostWrapperEl);
            }
            catch (e) {
                console.error(e);
            }
        }
    }
    writeValue(obj) {
        if (this.valueContainerEl) {
            this._renderer.setProperty(this.valueContainerEl, 'innerText', obj);
        }
        this._renderer.setProperty(this._elementRef.nativeElement, 'value', obj);
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        if (this.hostEl) {
            this._renderer.setProperty(this.hostEl, 'disabled', isDisabled);
        }
    }
    onChange(_) {
        this.writeValue(_);
    }
    onTouched(_) {
        if (this.control) {
            this.isInvalid = this.control.invalid;
        }
    }
    createHostWrapperContainer() {
        const wrapper = this._renderer.createElement('div');
        this._renderer.addClass(wrapper, 'eui-textarea__wrapper');
        if (this.readonly) {
            this._renderer.addClass(wrapper, 'eui-textarea__wrapper--readonly');
            this._renderer.addClass(this.hostEl, 'eui-textarea--readonly');
        }
        else {
            this._renderer.removeClass(wrapper, 'eui-textarea__wrapper--readonly');
            this._renderer.removeClass(this.hostEl, 'eui-textarea--readonly');
        }
        return wrapper;
    }
    /**
     * Create a Div element which will hold the value to display when readonly
     * @private
     */
    createValueContainer() {
        const valueContainer = this._renderer.createElement('div');
        this._renderer.addClass(valueContainer, 'eui-textarea__value-container');
        this._renderer.setProperty(valueContainer, 'innerText', this.hostEl.value);
        return valueContainer;
    }
    /**
     * Changes the markup around the textarea. Append textarea onto a container with div
     * @private
     */
    handleMarkup() {
        if (this.hostParentEl != null) {
            this.hostParentEl.replaceChild(this.hostWrapperEl, this.hostEl);
            this.hostWrapperEl.appendChild(this.hostEl);
            this.hostWrapperEl.appendChild(this.valueContainerEl);
        }
    }
}
EuiTextareaComponent.idCounter = 0;
/** @nocollapse */ EuiTextareaComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiTextareaComponent, deps: [{ token: i0.Injector }, { token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ EuiTextareaComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.0.3", type: EuiTextareaComponent, selector: "textarea[euiTextArea]", inputs: { class: "class", disabled: "disabled", readonly: "readonly", id: "id", isInvalid: "isInvalid", e2eAttr: "e2eAttr" }, host: { listeners: { "input": "onInputChange($event.target.value)", "blur": "onBlur($event.target.value)" }, properties: { "class": "this.class", "attr.disabled": "this.disabled", "attr.readonly": "this.readonly", "attr.id": "this.id", "attr.data-e2e": "this.e2eAttr" } }, providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef((() => EuiTextareaComponent)), multi: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [".eui-textarea__wrapper{display:flex;position:relative;width:100%}.eui-textarea__wrapper .eui-textarea{font: 400 1rem/1.25rem arial,sans-serif;-webkit-appearance:none;appearance:none;background-color:var(--eui-base-color-white);border:1px solid var(--eui-base-color-grey-50);border-radius:var(--eui-base-border-radius);color:var(--eui-base-color-grey-100);display:block;padding:calc(var(--eui-base-spacing-s) - 1px);width:100%;height:auto;resize:vertical}.eui-textarea__wrapper .eui-textarea:active:not([readonly]),.eui-textarea__wrapper .eui-textarea:focus:not([readonly]){z-index:auto;border-color:var(--eui-base-color-accessible-focus)!important;box-shadow:inset 0 0 0 1px var(--eui-base-color-accessible-focus),inset 0 0 0 2px var(--eui-base-color-grey-5);transition:border-color .15s ease-in-out,box-shadow .15s ease-in-out}.eui-textarea__wrapper .eui-textarea[readonly]{background-color:transparent;border-color:transparent!important;box-shadow:none;height:inherit;padding:var(--eui-base-spacing-2xs) 0;pointer-events:none}.eui-textarea__wrapper .eui-textarea[readonly]+.eui-feedback-message,.eui-textarea__wrapper .eui-textarea[readonly]+.eui-input-maxlength{-webkit-appearance:none;appearance:none;display:none}.eui-textarea__wrapper .eui-textarea[disabled],.eui-textarea__wrapper .eui-textarea--disabled{background-color:var(--eui-base-color-grey-5);border:1px solid var(--eui-base-color-grey-25);color:var(--eui-base-color-grey-75);pointer-events:none}.eui-textarea__wrapper .eui-textarea--invalid,.eui-textarea__wrapper .eui-textarea--danger{border:1px solid var(--eui-base-color-danger-100)}.eui-textarea__wrapper .eui-textarea--clearable{padding-right:var(--eui-base-spacing-2xl)}.eui-textarea__wrapper .eui-textarea--clearable-icon{visibility:visible;position:absolute;right:var(--eui-base-spacing-s);cursor:pointer;-webkit-user-select:none;user-select:none}.eui-textarea__wrapper .eui-textarea--clearable-icon svg.eui-icon-svg{fill:var(--eui-base-color-grey-35)}.eui-textarea__wrapper .eui-textarea--clearable-icon svg.eui-icon-svg:hover{fill:var(--eui-base-color-grey-100)}.eui-textarea__wrapper .eui-textarea--loading{padding-right:var(--eui-base-spacing-2xl)}.eui-textarea__wrapper .eui-textarea--loading-icon{align-items:center;display:flex}.eui-textarea__wrapper .eui-textarea--loading-icon:after,.eui-textarea__wrapper .eui-textarea--loading-icon:before{content:\"\";height:var(--eui-base-spacing-l);position:absolute;right:var(--eui-base-spacing-s);width:var(--eui-base-spacing-l)}.eui-textarea__wrapper .eui-textarea--loading-icon:before{border:var(--eui-base-spacing-2xs) solid rgba(0,0,0,.35);border-radius:100%}.eui-textarea__wrapper .eui-textarea--loading-icon:after{animation:eui-loading .6s linear;animation-iteration-count:infinite;border-color:var(--eui-base-color-white) transparent transparent;border-radius:100%;border-style:solid;border-width:var(--eui-base-spacing-2xs);box-shadow:0 0 0 1px transparent}.eui-textarea__wrapper .eui-textarea--loading-icon--sm:after,.eui-textarea__wrapper .eui-textarea--loading-icon--sm:before{height:var(--eui-base-spacing-m);width:var(--eui-base-spacing-m)}@keyframes eui-loading{to{transform:rotate(360deg)}}.eui-textarea__wrapper .eui-textarea::-webkit-scrollbar{display:inherit;height:8px;width:8px;background-color:var(--eui-base-color-grey-10)}.eui-textarea__wrapper .eui-textarea::-webkit-scrollbar-thumb{background-color:var(--eui-base-color-grey-20);border-radius:5rem}.eui-textarea__wrapper .eui-textarea::-webkit-scrollbar-thumb:hover{background-color:var(--eui-base-color-grey-25)}.eui-textarea__wrapper .eui-textarea::-webkit-scrollbar-track{background-color:var(--eui-base-color-grey-10);border-radius:0}.eui-textarea__wrapper .eui-textarea--disabled,.eui-textarea__wrapper .eui-textarea[disabled]{pointer-events:auto}.eui-textarea__wrapper .eui-textarea__value-container{-webkit-appearance:none;appearance:none;display:none}.eui-textarea__wrapper--readonly{padding:var(--eui-base-spacing-2xs) 0}.eui-textarea__wrapper--readonly .eui-input-maxlength-wrapper{-webkit-appearance:none;appearance:none;display:none}.eui-textarea__wrapper--readonly .eui-textarea__value-container{-webkit-appearance:initial;appearance:initial;color:var(--eui-base-color-grey-90);display:inline-block;word-break:break-word}.eui-textarea__wrapper .eui-textarea--readonly{-webkit-appearance:none;appearance:none;display:none}.eui-textarea__wrapper .eui-textarea--invalid{border:1px solid var(--eui-base-color-danger-100)}\n"], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiTextareaComponent, decorators: [{
            type: Component,
            args: [{ selector: 'textarea[euiTextArea]', template: '<ng-content></ng-content>', encapsulation: ViewEncapsulation.None, providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef((() => EuiTextareaComponent)), multi: true }], styles: [".eui-textarea__wrapper{display:flex;position:relative;width:100%}.eui-textarea__wrapper .eui-textarea{font: 400 1rem/1.25rem arial,sans-serif;-webkit-appearance:none;appearance:none;background-color:var(--eui-base-color-white);border:1px solid var(--eui-base-color-grey-50);border-radius:var(--eui-base-border-radius);color:var(--eui-base-color-grey-100);display:block;padding:calc(var(--eui-base-spacing-s) - 1px);width:100%;height:auto;resize:vertical}.eui-textarea__wrapper .eui-textarea:active:not([readonly]),.eui-textarea__wrapper .eui-textarea:focus:not([readonly]){z-index:auto;border-color:var(--eui-base-color-accessible-focus)!important;box-shadow:inset 0 0 0 1px var(--eui-base-color-accessible-focus),inset 0 0 0 2px var(--eui-base-color-grey-5);transition:border-color .15s ease-in-out,box-shadow .15s ease-in-out}.eui-textarea__wrapper .eui-textarea[readonly]{background-color:transparent;border-color:transparent!important;box-shadow:none;height:inherit;padding:var(--eui-base-spacing-2xs) 0;pointer-events:none}.eui-textarea__wrapper .eui-textarea[readonly]+.eui-feedback-message,.eui-textarea__wrapper .eui-textarea[readonly]+.eui-input-maxlength{-webkit-appearance:none;appearance:none;display:none}.eui-textarea__wrapper .eui-textarea[disabled],.eui-textarea__wrapper .eui-textarea--disabled{background-color:var(--eui-base-color-grey-5);border:1px solid var(--eui-base-color-grey-25);color:var(--eui-base-color-grey-75);pointer-events:none}.eui-textarea__wrapper .eui-textarea--invalid,.eui-textarea__wrapper .eui-textarea--danger{border:1px solid var(--eui-base-color-danger-100)}.eui-textarea__wrapper .eui-textarea--clearable{padding-right:var(--eui-base-spacing-2xl)}.eui-textarea__wrapper .eui-textarea--clearable-icon{visibility:visible;position:absolute;right:var(--eui-base-spacing-s);cursor:pointer;-webkit-user-select:none;user-select:none}.eui-textarea__wrapper .eui-textarea--clearable-icon svg.eui-icon-svg{fill:var(--eui-base-color-grey-35)}.eui-textarea__wrapper .eui-textarea--clearable-icon svg.eui-icon-svg:hover{fill:var(--eui-base-color-grey-100)}.eui-textarea__wrapper .eui-textarea--loading{padding-right:var(--eui-base-spacing-2xl)}.eui-textarea__wrapper .eui-textarea--loading-icon{align-items:center;display:flex}.eui-textarea__wrapper .eui-textarea--loading-icon:after,.eui-textarea__wrapper .eui-textarea--loading-icon:before{content:\"\";height:var(--eui-base-spacing-l);position:absolute;right:var(--eui-base-spacing-s);width:var(--eui-base-spacing-l)}.eui-textarea__wrapper .eui-textarea--loading-icon:before{border:var(--eui-base-spacing-2xs) solid rgba(0,0,0,.35);border-radius:100%}.eui-textarea__wrapper .eui-textarea--loading-icon:after{animation:eui-loading .6s linear;animation-iteration-count:infinite;border-color:var(--eui-base-color-white) transparent transparent;border-radius:100%;border-style:solid;border-width:var(--eui-base-spacing-2xs);box-shadow:0 0 0 1px transparent}.eui-textarea__wrapper .eui-textarea--loading-icon--sm:after,.eui-textarea__wrapper .eui-textarea--loading-icon--sm:before{height:var(--eui-base-spacing-m);width:var(--eui-base-spacing-m)}@keyframes eui-loading{to{transform:rotate(360deg)}}.eui-textarea__wrapper .eui-textarea::-webkit-scrollbar{display:inherit;height:8px;width:8px;background-color:var(--eui-base-color-grey-10)}.eui-textarea__wrapper .eui-textarea::-webkit-scrollbar-thumb{background-color:var(--eui-base-color-grey-20);border-radius:5rem}.eui-textarea__wrapper .eui-textarea::-webkit-scrollbar-thumb:hover{background-color:var(--eui-base-color-grey-25)}.eui-textarea__wrapper .eui-textarea::-webkit-scrollbar-track{background-color:var(--eui-base-color-grey-10);border-radius:0}.eui-textarea__wrapper .eui-textarea--disabled,.eui-textarea__wrapper .eui-textarea[disabled]{pointer-events:auto}.eui-textarea__wrapper .eui-textarea__value-container{-webkit-appearance:none;appearance:none;display:none}.eui-textarea__wrapper--readonly{padding:var(--eui-base-spacing-2xs) 0}.eui-textarea__wrapper--readonly .eui-input-maxlength-wrapper{-webkit-appearance:none;appearance:none;display:none}.eui-textarea__wrapper--readonly .eui-textarea__value-container{-webkit-appearance:initial;appearance:initial;color:var(--eui-base-color-grey-90);display:inline-block;word-break:break-word}.eui-textarea__wrapper .eui-textarea--readonly{-webkit-appearance:none;appearance:none;display:none}.eui-textarea__wrapper .eui-textarea--invalid{border:1px solid var(--eui-base-color-danger-100)}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.Injector }, { type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }, {
                type: Input
            }], disabled: [{
                type: HostBinding,
                args: ['attr.disabled']
            }, {
                type: Input
            }], readonly: [{
                type: HostBinding,
                args: ['attr.readonly']
            }, {
                type: Input
            }], id: [{
                type: HostBinding,
                args: ['attr.id']
            }, {
                type: Input
            }], isInvalid: [{
                type: Input
            }], e2eAttr: [{
                type: HostBinding,
                args: ['attr.data-e2e']
            }, {
                type: Input
            }], onInputChange: [{
                type: HostListener,
                args: ['input', ['$event.target.value']]
            }], onBlur: [{
                type: HostListener,
                args: ['blur', ['$event.target.value']]
            }] } });

class AutoResizeDirective {
    constructor(el, renderer, control) {
        this.el = el;
        this.renderer = renderer;
        this.control = control;
        this.defaultRows = 1;
        this.destroy$ = new Subject();
        this._autoResize = true;
    }
    get autoResize() {
        return this._autoResize;
    }
    set autoResize(state) {
        this._autoResize = coerceBooleanProperty(state);
    }
    set minRows(minRows) {
        this.defaultRows = coerceNumberProperty(minRows);
    }
    onInput() {
        this.resize();
    }
    ngOnInit() {
        fromEvent(window, 'resize')
            .pipe(takeUntil(this.destroy$), debounceTime(300))
            .subscribe(() => this.resize());
        // Create an observer instance linked to the callback function
        this.observer = new MutationObserver(this.readonlyMutationObserver.bind(this));
        // Start observing the target node for configured mutations
        this.observer.observe(this.el.nativeElement, { attributes: true });
    }
    ngOnChanges(changes) {
        if (changes['autoResize']) {
            const enable = changes['autoResize'].currentValue || this.autoResize;
            this.renderer.setProperty(this.el.nativeElement, 'rows', this.defaultRows);
            this.renderer.setStyle(this.el.nativeElement, 'overflow', enable ? 'hidden' : '');
            this.renderer.setStyle(this.el.nativeElement, 'resize', enable ? 'none' : '');
            if (enable) {
                this.resize();
            }
        }
    }
    ngAfterViewInit() {
        if (this.autoResize) {
            if (this.control) {
                this.handleFormControlChanges();
            }
            else {
                this.resize();
            }
        }
    }
    ngOnDestroy() {
        this.destroy$.complete();
        this.destroy$.unsubscribe();
        this.observer.disconnect();
    }
    /**
     * Calculates the height and set it, causing the textarea to resize.
     */
    resize(heightAuto = true) {
        if (this.autoResize) {
            // clone element
            const clone = this.el.nativeElement.cloneNode(true);
            const parent = this.el.nativeElement.parentNode;
            clone.style.width = this.el.nativeElement.offsetWidth + 'px';
            clone.style.visibility = 'hidden';
            clone.style.position = 'absolute';
            clone.textContent = this.el.nativeElement.value;
            parent.appendChild(clone);
            clone.style['overflow-y'] = 'hidden';
            clone.style.height = 'auto';
            let cloneHeight = clone.scrollHeight;
            this.renderer.setStyle(clone, 'height', cloneHeight);
            // calculate height
            let { offsetHeight, scrollHeight } = clone;
            let height = (offsetHeight > scrollHeight ? offsetHeight : scrollHeight);
            height = height <= 0 ? 'auto' : `${height}px`;
            // remove clone
            parent.removeChild(clone);
            // set height
            this.renderer.setStyle(this.el.nativeElement, 'height', height);
        }
    }
    /**
     * handles value changes through FormControl
     * @protected
     */
    handleFormControlChanges() {
        // in case value changed manually through control, call resize()
        // startWith is being used to cause resize to be called in case of late subscription after value has changed
        this.control.valueChanges.pipe(takeUntil(this.destroy$), startWith('')).subscribe(() => this.resize());
    }
    readonlyMutationObserver(mutationsList, observer) {
        // Use traditional 'for loops' for IE 11
        for (const mutation of mutationsList) {
            if (mutation.type === 'attributes' && mutation.attributeName === 'readonly') {
                this.resize();
            }
        }
    }
}
/** @nocollapse */ AutoResizeDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: AutoResizeDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i1.NgControl, optional: true }], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ AutoResizeDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.0.3", type: AutoResizeDirective, selector: "textarea[autoResize]", inputs: { autoResize: "autoResize", minRows: "minRows" }, host: { listeners: { "input": "onInput($event.target)" } }, usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: AutoResizeDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: 'textarea[autoResize]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i1.NgControl, decorators: [{
                    type: Optional
                }] }]; }, propDecorators: { autoResize: [{
                type: Input
            }], minRows: [{
                type: Input
            }], onInput: [{
                type: HostListener,
                args: ['input', ['$event.target']]
            }] } });

class EuiTextAreaModule {
}
/** @nocollapse */ EuiTextAreaModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiTextAreaModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ EuiTextAreaModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.3", ngImport: i0, type: EuiTextAreaModule, declarations: [EuiTextareaComponent,
        AutoResizeDirective], imports: [CommonModule], exports: [EuiTextareaComponent,
        AutoResizeDirective] });
/** @nocollapse */ EuiTextAreaModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiTextAreaModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiTextAreaModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                    ],
                    declarations: [
                        EuiTextareaComponent,
                        AutoResizeDirective
                    ],
                    exports: [
                        EuiTextareaComponent,
                        AutoResizeDirective
                    ],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AutoResizeDirective, EuiTextAreaModule, EuiTextareaComponent };
